import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getMovieById, getCategoryName, getRelatedMovies, formatDuration } from "@/utils/movie";
import { reviews } from "@/mocks/reviews";
import MovieCard from "@/components/feature/MovieCard";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ReviewSection from "@/pages/detail/components/ReviewSection";
import SharePanel from "@/pages/detail/components/SharePanel";
import { useFavorites } from "@/hooks/useFavorites";
import { useSeo } from "@/hooks/useSeo";

export default function DetailPage() {
  const { id = "" } = useParams();
  const movie = getMovieById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [shareOpen, setShareOpen] = useState(false);
  const [toast, setToast] = useState("");

  useSeo({
    title: movie ? `${movie.title}详情介绍 - 黄色大片` : "影片详情 - 黄色大片",
    description: movie
      ? `${movie.title}：${movie.summary} 导演 ${movie.director}，${movie.year} 年上映。黄色大片为你提供影片资料与影评。`
      : "黄色大片影片详情页。",
    keywords: movie ? `${movie.title},${movie.title}影评,${movie.title}介绍,黄色大片` : "影片详情,黄色大片",
  });

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(""), 2200);
    return () => clearTimeout(timer);
  }, [toast]);

  if (!movie) {
    return (
      <div className="mx-auto flex max-w-7xl flex-col items-center px-4 py-24 text-center md:px-6">
        <i className="ri-film-line text-5xl text-foreground-500" />
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-50">未找到该影片</h1>
        <p className="mt-2 text-sm text-foreground-400">影片可能已下线或链接有误，去看看其他推荐吧。</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 font-label text-sm uppercase tracking-wide text-foreground-950 transition-colors hover:bg-primary-400"
        >
          <i className="ri-home-4-line text-lg" />
          返回首页
        </Link>
      </div>
    );
  }

  const movieReviews = reviews.filter((review) => review.movieId === movie.id);
  const related = getRelatedMovies(movie, 4);
  const favorite = isFavorite(movie.id);

  const handleFavorite = () => {
    const added = toggleFavorite(movie.id);
    setToast(added ? "已加入收藏，可在本机随时查看" : "已取消收藏");
  };

  const handleShare = (platform: string) => {
    const url = typeof window !== "undefined" ? window.location.href : "";
    try {
      navigator.clipboard?.writeText(url);
    } catch {
      /* clipboard may be unavailable */
    }
    setShareOpen(false);
    setToast(
      platform === "复制链接" ? "影片链接已复制到剪贴板" : `链接已复制，快去${platform}分享给朋友吧`,
    );
  };

  const metaRows = [
    { label: "导演", value: movie.director },
    { label: "主演", value: movie.cast.join(" / ") },
    { label: "上映日期", value: movie.releaseDate },
    { label: "片长", value: formatDuration(movie.duration) },
    { label: "地区", value: movie.region },
    { label: "类型", value: movie.genres.map((genre) => getCategoryName(genre)).join(" / ") },
  ];

  return (
    <div>
      <div className="relative w-full overflow-hidden bg-background-50">
        <div className="absolute inset-0">
          <img
            src={movie.cover}
            alt={`${movie.title} 剧照`}
            title={`${movie.title} - 黄色大片 影片详情`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/85 to-background-50/70" />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 pb-10 pt-6 md:px-6 md:pb-12 md:pt-8">
          <Breadcrumbs
            items={[
              { label: "首页", to: "/" },
              { label: "电影分类", to: `/category/${movie.genres[0]}` },
              { label: getCategoryName(movie.genres[0]), to: `/category/${movie.genres[0]}` },
              { label: movie.title },
            ]}
          />

          <div className="mt-6 flex flex-col gap-7 md:flex-row md:gap-10">
            <div className="w-40 flex-shrink-0 sm:w-48 md:w-56">
              <div className="overflow-hidden rounded-lg border border-background-300/50" style={{ aspectRatio: "2 / 3" }}>
                <img
                  src={movie.poster}
                  alt={`${movie.title}（${movie.year}）电影海报`}
                  title={`${movie.title} - 黄色大片 影片详情`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <div className="mt-4 flex flex-col gap-2">
                <button
                  type="button"
                  onClick={handleFavorite}
                  className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full px-5 py-3 font-label text-sm uppercase tracking-wide transition-colors ${
                    favorite
                      ? "bg-accent-500 text-foreground-950"
                      : "bg-primary-500 text-foreground-950 hover:bg-primary-400"
                  }`}
                >
                  <i className={`${favorite ? "ri-heart-3-fill" : "ri-heart-3-line"} text-lg`} />
                  {favorite ? "已收藏" : "收藏影片"}
                </button>
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-background-300/60 px-5 py-3 font-label text-sm uppercase tracking-wide text-foreground-100 transition-colors hover:border-primary-400 hover:text-primary-400"
                >
                  <i className="ri-share-forward-line text-lg" />
                  分享
                </button>
              </div>
            </div>

            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3">
                {movie.genres.map((genre) => (
                  <Link
                    key={genre}
                    to={`/category/${genre}`}
                    className="rounded-full bg-primary-100 px-3 py-1 text-xs text-primary-400 transition-colors hover:bg-primary-500 hover:text-foreground-950"
                  >
                    {getCategoryName(genre)}
                  </Link>
                ))}
              </div>
              <h1 className="mt-4 font-heading text-3xl font-black text-foreground-50 md:text-4xl">{movie.title}</h1>
              <p className="mt-2 text-sm text-foreground-500">{movie.originalTitle}</p>

              <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-3">
                <div className="flex items-baseline gap-1.5">
                  <span className="font-heading text-3xl font-black text-primary-400">{movie.rating.toFixed(1)}</span>
                  <span className="text-xs text-foreground-500">/ 10</span>
                </div>
                <span className="flex items-center gap-1.5 text-sm text-foreground-300">
                  <i className="ri-fire-line text-base text-accent-400" />
                  热度 {movie.heat}
                </span>
                <span className="flex items-center gap-1.5 text-sm text-foreground-300">
                  <i className="ri-calendar-line text-base text-primary-400" />
                  {movie.releaseDate}
                </span>
                <span className="flex items-center gap-1.5 text-sm text-foreground-300">
                  <i className="ri-time-line text-base text-primary-400" />
                  {formatDuration(movie.duration)}
                </span>
              </div>

              <p className="mt-5 max-w-3xl text-sm leading-relaxed text-foreground-200 md:text-base">
                {movie.summary}
              </p>

              <div className="mt-6 flex flex-wrap gap-2">
                {movie.tags.map((tag) => (
                  <span key={tag} className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs text-secondary-500">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 pb-16 md:px-6">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <section className="mt-2">
              <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-50">
                <i className="ri-book-open-line text-primary-400" />
                剧情简介
              </h2>
              <div className="mt-4 rounded-lg border border-background-200/60 bg-background-100 p-5 md:p-6">
                <p className="text-sm leading-loose text-foreground-300 md:text-[15px]">{movie.synopsis}</p>
              </div>
            </section>

            <ReviewSection reviews={movieReviews} movieTitle={movie.title} />
          </div>

          <aside className="lg:col-span-1">
            <div className="sticky top-24 mt-2 flex flex-col gap-6">
              <div className="rounded-lg border border-background-200/60 bg-background-100 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-50">影片资料</h2>
                <dl className="mt-4 flex flex-col gap-3">
                  {metaRows.map((row) => (
                    <div key={row.label} className="flex gap-3 text-sm">
                      <dt className="w-16 flex-shrink-0 text-foreground-500">{row.label}</dt>
                      <dd className="flex-1 text-foreground-200">{row.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>

              <div className="rounded-lg border border-background-200/60 bg-background-100 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-50">浏览分类</h2>
                <div className="mt-4 flex flex-wrap gap-2">
                  {movie.genres.map((genre) => (
                    <Link
                      key={genre}
                      to={`/category/${genre}`}
                      className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-background-200/60 px-3 py-1.5 text-sm text-foreground-200 transition-colors hover:bg-primary-100 hover:text-primary-400"
                    >
                      <i className="ri-price-tag-3-line text-sm" />
                      {getCategoryName(genre)}片
                    </Link>
                  ))}
                </div>
              </div>

              <div className="rounded-lg border border-background-200/60 bg-background-100 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-50">免责声明</h2>
                <p className="mt-3 text-xs leading-relaxed text-foreground-500">
                  本页面所有影片资料、影评与评分为演示用虚构内容，本站不提供任何影片播放与下载服务。
                </p>
              </div>
            </div>
          </aside>
        </div>

        <section className="mt-14">
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-50">
            <i className="ri-movie-line text-primary-400" />
            相关影片推荐
          </h2>
          <div className="mt-5 grid grid-cols-2 gap-4 md:grid-cols-4">
            {related.map((item) => (
              <MovieCard key={item.id} movie={item} />
            ))}
          </div>
        </section>
      </div>

      <SharePanel open={shareOpen} title={movie.title} onClose={() => setShareOpen(false)} onShare={handleShare} />

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-[70] -translate-x-1/2 animate-fade-up rounded-full border border-background-300/60 bg-background-100 px-5 py-3 text-sm text-foreground-50">
          <span className="flex items-center gap-2">
            <i className="ri-checkbox-circle-fill text-primary-400" />
            {toast}
          </span>
        </div>
      )}
    </div>
  );
}